import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Fingerprint, X, Check, ShieldCheck, Fire, Lock, ArrowRight } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Produktionssicherer Check: Dev-/Test-Bypass nur außerhalb der Produktion zeigen
const IS_PRODUCTION = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENV === 'production';

const LS_KEY = 'archive_feld1_draft';

const ERWARTUNG_TAGS = [
  { id: 'radikale_neuorientierung', label: 'Radikale Neuorientierung', desc: 'Alles auf Anfang. Neuer Pfad.' },
  { id: 'system_effizienz', label: 'System-Effizienz', desc: '20% Einsatz, 80% Output.' },
  { id: 'karriere_dominanz', label: 'Karriere-Dominanz', desc: 'Aufstieg. Einfluss. Kontrolle.' },
  { id: 'selbsterkenntnis', label: 'Selbsterkenntnis', desc: 'Wer bin ich wirklich?' }
];

const ENERGIE_KATEGORIEN = [
  { id: 'koerperlich_extrem', label: 'Koerperlich / Extrem' },
  { id: 'intellektuell_strategisch', label: 'Intellektuell / Strategisch' },
  { id: 'kreativ_schaffend', label: 'Kreativ / Schaffend' },
  { id: 'meditativ_rueckzug', label: 'Meditativ / Rueckzug' }
];

const GESCHLECHT_OPTIONS = [
  { value: 'maennlich', label: 'M' },
  { value: 'weiblich', label: 'W' },
  { value: 'divers', label: 'D' },
  { value: 'keine_angabe', label: 'K.A.' }
];

const INTENSITAET_LABELS = [
  { range: [1, 3], label: 'SPORADISCH', desc: 'Ich mache es, wenn Zeit ist.' },
  { range: [4, 7], label: 'AMBITIONIERT', desc: 'Regelmaessige Praxis, messbare Fortschritte.' },
  { range: [8, 10], label: 'OBSESSIV', desc: 'Teil meiner Identitaet. Signifikante Ressourcen.' }
];

const getIntensitaetInfo = (val) => {
  if (val <= 3) return INTENSITAET_LABELS[0];
  if (val <= 7) return INTENSITAET_LABELS[1];
  return INTENSITAET_LABELS[2];
};

const LEBENSSTATUS_OPTIONS = [
  { value: 'schueler', label: 'Schueler/in' },
  { value: 'student', label: 'Student/in' },
  { value: 'angestellt', label: 'Angestellt' },
  { value: 'selbststaendig', label: 'Selbststaendig' },
  { value: 'neuorientierung', label: 'Neuorientierung' }
];

const Feld1Overlay = ({ sessionId, onSealed, onClose }) => {
  const [form, setForm] = useState({
    name: '', geburtsdatum: '', groesse_cm: '', gewicht_kg: '', aktueller_job: '', erwartung: '', geschlecht: '', lebensstatus: '',
    leidenschaft_hobby: '', intensitaet: 5, energie_kategorien: []
  });
  const [sealing, setSealing] = useState(false);
  const [sealProgress, setSealProgress] = useState(0);
  const [sealed, setSealed] = useState(false);
  const [sealResult, setSealResult] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [testBypassLoading, setTestBypassLoading] = useState(false);

  const [formLoaded, setFormLoaded] = useState(false);

  useEffect(() => {
    // Load saved draft first
    const saved = sessionStorage.getItem(`${LS_KEY}_${sessionId}`);
    if (saved) {
      try { setForm(JSON.parse(saved)); } catch {}
    }
    // Then merge onboarding data from landing page (name, alter → geburtsdatum)
    const onboard = sessionStorage.getItem(`deepdive_onboard_${sessionId}`);
    if (onboard) {
      try {
        const data = JSON.parse(onboard);
        setForm(prev => ({
          ...prev,
          name: prev.name || data.name || '',
          geburtsdatum: prev.geburtsdatum || (data.alter ? `${new Date().getFullYear() - data.alter}-01-01` : ''),
        }));
        sessionStorage.removeItem(`deepdive_onboard_${sessionId}`);
      } catch {}
    }
    setFormLoaded(true);
  }, [sessionId]);

  useEffect(() => {
    if (formLoaded) {
      sessionStorage.setItem(`${LS_KEY}_${sessionId}`, JSON.stringify(form));
    }
  }, [form, sessionId, formLoaded]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const isValid = form.name.trim() && form.geburtsdatum && form.groesse_cm && form.gewicht_kg && form.aktueller_job.trim() && form.erwartung;

  const handleSeal = () => {
    if (!isValid) return;
    performSeal();
  };

  const handleCheckout = async () => {
    setPaymentLoading(true);
    try {
      const res = await axios.post(`${API}/create-checkout-session`, { session_id: sessionId, origin_url: window.location.origin });
      if (res.data.url || res.data.checkout_url) window.location.href = res.data.url || res.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      setPaymentLoading(false);
    }
  };

  const handleTestBypass = async () => {
    setTestBypassLoading(true);
    try {
      await axios.post(`${API}/sessions/${sessionId}/unlock-deep-scan`);
      setShowPaywall(false);
      performSeal();
    } catch (error) {
      console.error('Bypass error:', error);
      setTestBypassLoading(false);
    }
  };

  const performSeal = async () => {
    setSealing(true);
    const interval = setInterval(() => {
      setSealProgress(prev => {
        if (prev >= 100) { clearInterval(interval); return 100; }
        return prev + 2;
      });
    }, 30);

    try {
      const res = await axios.post(`${API}/user/feld1`, {
        session_id: sessionId,
        name: form.name.trim(),
        geburtsdatum: form.geburtsdatum,
        groesse_cm: parseInt(form.groesse_cm),
        gewicht_kg: parseFloat(form.gewicht_kg),
        aktueller_job: form.aktueller_job.trim(),
        erwartung: form.erwartung,
        geschlecht: form.geschlecht || null,
        lebensstatus: form.lebensstatus || null,
        leidenschaft_hobby: form.leidenschaft_hobby.trim() || null,
        intensitaet: form.leidenschaft_hobby.trim() ? form.intensitaet : null,
        energie_kategorien: form.energie_kategorien.length > 0 ? form.energie_kategorien : null
      });

      await new Promise(r => setTimeout(r, 1800));
      clearInterval(interval);
      setSealProgress(100);
      setSealResult(res.data.feld1);
      setSealed(true);
      sessionStorage.removeItem(`${LS_KEY}_${sessionId}`);
      setTimeout(() => { onSealed(res.data.feld1); }, 2500);
    } catch (err) {
      console.error('Seal error:', err);
      setSealing(false);
      setSealProgress(0);
      clearInterval(interval);
    }
  };

  // Shared styles
  const inputStyle = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)',
    backdropFilter: 'var(--glass-blur)',
    borderRadius: '12px',
    transition: 'all 0.2s ease'
  };

  const inputFocusStyle = (filled) => ({
    ...inputStyle,
    borderColor: filled ? 'var(--accent-cyan)' : 'var(--border-default)'
  });

  const tagStyle = (selected, color = 'var(--accent-cyan)') => ({
    background: selected ? color : 'var(--bg-surface)',
    border: `1px solid ${selected ? color : 'var(--border-default)'}`,
    color: selected ? '#fff' : 'var(--text-primary)',
    cursor: 'pointer',
    borderRadius: '12px',
    backdropFilter: 'var(--glass-blur)',
    transition: 'all 0.2s ease'
  });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(16px)' }}
        data-testid="feld1-overlay"
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-lg relative"
          style={{
            background: 'var(--bg-surface)',
            backdropFilter: 'blur(24px)',
            border: '1px solid var(--border-default)',
            borderRadius: '24px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.12)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6" style={{ borderBottom: '1px solid var(--border-default)' }}>
            <div className="flex items-center gap-3">
              <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: 'var(--accent-cyan)' }}>
                <Fingerprint size={20} weight="bold" color="#fff" />
              </div>
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Feld 1 — Identitaet</h2>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Stammdaten erfassen</p>
              </div>
            </div>
            {!sealing && (
              <button onClick={onClose} className="p-2 rounded-full transition-all hover:scale-110" style={{ color: 'var(--text-secondary)', background: 'var(--bg-surface)' }} data-testid="feld1-close">
                <X size={18} />
              </button>
            )}
          </div>

          {/* ========== PAYWALL ========== */}
          {showPaywall && !sealing && !sealed && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-6">
              <div className="text-center mb-6">
                <div className="hex-clip mx-auto flex items-center justify-center mb-4" style={{ width: 64, height: 64, background: 'var(--accent-gold)' }}>
                  <Lock size={28} weight="bold" color="#fff" />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Zugang freischalten</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  Deine Identitaet wird versiegelt und das volle Assessment freigeschaltet.
                </p>
              </div>

              <div className="p-4 mb-5" style={{ ...inputStyle, borderRadius: '16px' }}>
                <p className="text-xs font-bold mb-3" style={{ color: 'var(--text-secondary)', letterSpacing: '0.1em' }}>DAS ERWARTET DICH:</p>
                <ul className="space-y-2">
                  {['5 Analyse-Bloecke (Geist, Koerper, Wesen, Biografie)', 'KI-generiertes Elite-Dossier', 'Karriere-Navigator mit Job/Studium/Gruendung', 'Strategisches Hexagon-Board mit Mission Briefings'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check size={14} style={{ color: '#22C55E' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="text-center mb-5">
                <span className="text-4xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>19,99</span>
                <span className="text-sm ml-1" style={{ color: 'var(--text-secondary)' }}>EUR · EINMALIG</span>
              </div>

              <button
                onClick={handleCheckout}
                disabled={paymentLoading}
                className="w-full py-3 flex items-center justify-center gap-3 font-bold transition-all text-white"
                style={{ background: 'var(--accent-cyan)', borderRadius: '9999px', border: 'none', cursor: 'pointer', boxShadow: '0 8px 24px rgba(6,182,212,0.25)' }}
                data-testid="feld1-checkout-btn"
              >
                {paymentLoading ? 'Wird verarbeitet...' : 'Jetzt freischalten'}
                <ArrowRight size={18} weight="bold" />
              </button>

              {!IS_PRODUCTION && (
                <button
                  onClick={handleTestBypass}
                  disabled={testBypassLoading}
                  className="w-full py-2 mt-3 flex items-center justify-center gap-2 transition-all"
                  style={{ border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', borderRadius: '9999px' }}
                  data-testid="feld1-test-bypass-btn"
                >
                  <span className="text-xs">{testBypassLoading ? 'Wird freigeschaltet...' : 'Test-Modus (ohne Zahlung)'}</span>
                </button>
              )}

              <button onClick={() => setShowPaywall(false)} className="w-full mt-3 text-center" style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Zurueck zum Formular</span>
              </button>

              <p className="text-center mt-3" style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                Sichere Zahlung ueber Stripe. Keine Abonnements.
              </p>
            </motion.div>
          )}

          {/* Sealed State */}
          {sealed && sealResult && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-8 text-center">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}>
                <div className="hex-clip mx-auto flex items-center justify-center mb-4" style={{ width: 80, height: 80, background: '#22C55E' }}>
                  <ShieldCheck size={36} weight="bold" color="#fff" />
                </div>
              </motion.div>
              <p className="text-xs mb-2" style={{ color: '#22C55E', letterSpacing: '0.2em', fontWeight: 600 }}>VERSCHLUESSELUNG ABGESCHLOSSEN</p>
              <h3 className="text-xl font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                {sealResult.name} | {sealResult.alter} J. | BMI {sealResult.bmi}
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sealResult.aktueller_job}</p>
              <p className="text-xs mt-3 px-4 py-1.5 inline-block rounded-full text-white font-bold" style={{ background: 'var(--accent-cyan)' }}>
                ZIEL: {ERWARTUNG_TAGS.find(t => t.id === sealResult.erwartung)?.label || sealResult.erwartung}
              </p>
              {sealResult.leidenschaft_hobby && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-3">
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Leidenschaft: <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{sealResult.leidenschaft_hobby}</span> | Intensitaet: <span style={{ fontWeight: 700, color: 'var(--accent-gold)' }}>{sealResult.intensitaet}/10</span> ({sealResult.intensitaet_label})
                  </p>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Sealing Animation */}
          {sealing && !sealed && (
            <div className="p-8">
              <div className="mb-6">
                <p className="text-xs mb-2 font-bold" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.15em' }}>
                  Versiegelung laeuft...
                </p>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--border-default)' }}>
                  <motion.div className="h-full rounded-full" style={{ background: 'var(--accent-cyan)' }} animate={{ width: `${sealProgress}%` }} />
                </div>
              </div>
              <div className="space-y-2">
                {['Identitaet verifiziert', 'Biometrie berechnet', 'Energie-Profil gesichert', 'Session gesichert', 'Daten versiegelt'].map((step, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: sealProgress >= (i + 1) * 20 ? 1 : 0.2, x: 0 }}
                    className="text-xs"
                    style={{ color: sealProgress >= (i + 1) * 20 ? '#22C55E' : 'var(--text-secondary)' }}
                  >
                    {sealProgress >= (i + 1) * 20 ? '✓' : '○'} {step}
                  </motion.p>
                ))}
              </div>
            </div>
          )}

          {/* Form */}
          {!sealing && !sealed && !showPaywall && (
            <div className="p-6 space-y-5">
              {/* Name */}
              <div>
                <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>Name</label>
                <input
                  type="text" value={form.name} onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Vor- und Nachname"
                  className="w-full p-3 text-sm focus:outline-none"
                  style={inputFocusStyle(form.name.trim())}
                  data-testid="feld1-name"
                />
              </div>

              {/* Geschlecht */}
              <div>
                <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>Geschlecht</label>
                <div className="grid grid-cols-4 gap-2">
                  {GESCHLECHT_OPTIONS.map(g => (
                    <button key={g.value} onClick={() => handleChange('geschlecht', g.value)}
                      className="py-2.5 text-center text-xs font-medium transition-all"
                      style={tagStyle(form.geschlecht === g.value)}
                      data-testid={`feld1-geschlecht-${g.value}`}
                    >{g.label}</button>
                  ))}
                </div>
              </div>

              {/* Lebensstatus */}
              <div>
                <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>Lebensstatus</label>
                <div className="grid grid-cols-3 gap-2">
                  {LEBENSSTATUS_OPTIONS.map(s => (
                    <button key={s.value} onClick={() => handleChange('lebensstatus', s.value)}
                      className="py-2.5 text-center text-xs font-medium transition-all"
                      style={tagStyle(form.lebensstatus === s.value)}
                      data-testid={`feld1-status-${s.value}`}
                    >{s.label}</button>
                  ))}
                </div>
              </div>

              {/* Geburtsdatum + Job */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>Geburtsdatum</label>
                  <input type="date" value={form.geburtsdatum} onChange={(e) => handleChange('geburtsdatum', e.target.value)}
                    className="w-full p-3 text-sm focus:outline-none"
                    style={{ ...inputFocusStyle(form.geburtsdatum), colorScheme: 'auto' }}
                    data-testid="feld1-geburtsdatum"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>Aktueller Job</label>
                  <input type="text" value={form.aktueller_job} onChange={(e) => handleChange('aktueller_job', e.target.value)}
                    placeholder="z.B. Product Manager"
                    className="w-full p-3 text-sm focus:outline-none"
                    style={inputFocusStyle(form.aktueller_job.trim())}
                    data-testid="feld1-job"
                  />
                </div>
              </div>

              {/* Biometrie */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>Groesse (cm)</label>
                  <input type="number" value={form.groesse_cm} onChange={(e) => handleChange('groesse_cm', e.target.value)}
                    placeholder="180" min="100" max="230"
                    className="w-full p-3 text-sm focus:outline-none"
                    style={inputFocusStyle(form.groesse_cm)}
                    data-testid="feld1-groesse"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>Gewicht (kg)</label>
                  <input type="number" value={form.gewicht_kg} onChange={(e) => handleChange('gewicht_kg', e.target.value)}
                    placeholder="80" min="30" max="250" step="0.1"
                    className="w-full p-3 text-sm focus:outline-none"
                    style={inputFocusStyle(form.gewicht_kg)}
                    data-testid="feld1-gewicht"
                  />
                </div>
              </div>

              {/* Erwartung Tags */}
              <div>
                <label className="text-xs font-semibold block mb-2" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>Erwartung</label>
                <div className="grid grid-cols-2 gap-2">
                  {ERWARTUNG_TAGS.map((tag) => {
                    const selected = form.erwartung === tag.id;
                    return (
                      <motion.button key={tag.id} whileTap={{ scale: 0.96 }}
                        onClick={() => handleChange('erwartung', tag.id)}
                        className="p-3 text-left transition-all"
                        style={tagStyle(selected)}
                        data-testid={`feld1-tag-${tag.id}`}
                      >
                        <span className="text-xs font-bold block" style={{ fontFamily: 'var(--font-heading)' }}>{tag.label}</span>
                        <span className="text-xs" style={{ color: selected ? 'rgba(255,255,255,0.7)' : 'var(--text-secondary)' }}>{tag.desc}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* ========== OPERATIVE ENERGIE ========== */}
              <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: '16px' }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="hex-clip flex items-center justify-center" style={{ width: 28, height: 28, background: 'var(--accent-gold)' }}>
                    <Fire size={14} weight="bold" color="#fff" />
                  </div>
                  <label className="text-xs font-bold" style={{ color: 'var(--accent-gold)', letterSpacing: '0.12em' }}>Operative Energie</label>
                </div>

                <div className="mb-3">
                  <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-secondary)' }}>Leidenschaft / Hobby</label>
                  <input type="text" value={form.leidenschaft_hobby} onChange={(e) => handleChange('leidenschaft_hobby', e.target.value)}
                    placeholder="Was tust du, wenn niemand zuschaut und kein Geld fliesst?"
                    className="w-full p-3 text-sm focus:outline-none"
                    style={inputFocusStyle(form.leidenschaft_hobby.trim())}
                    data-testid="feld1-leidenschaft"
                  />
                </div>

                {form.leidenschaft_hobby.trim() && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Intensitaet</label>
                      <span className="text-xs font-bold" style={{ color: 'var(--accent-gold)' }}>
                        {form.intensitaet}/10 — {getIntensitaetInfo(form.intensitaet).label}
                      </span>
                    </div>
                    <input type="range" min="1" max="10" value={form.intensitaet}
                      onChange={(e) => handleChange('intensitaet', parseInt(e.target.value))}
                      className="w-full h-2 appearance-none cursor-pointer rounded-full"
                      style={{
                        background: `linear-gradient(to right, var(--accent-gold) 0%, var(--accent-gold) ${((form.intensitaet - 1) / 9) * 100}%, var(--border-default) ${((form.intensitaet - 1) / 9) * 100}%, var(--border-default) 100%)`,
                        borderRadius: '9999px', outline: 'none', WebkitAppearance: 'none'
                      }}
                      data-testid="feld1-intensitaet"
                    />
                    <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary)' }}>{getIntensitaetInfo(form.intensitaet).desc}</p>
                  </motion.div>
                )}

                {form.leidenschaft_hobby.trim() && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                    <label className="text-xs font-semibold block mb-2" style={{ color: 'var(--text-secondary)' }}>Kategorie</label>
                    <div className="grid grid-cols-2 gap-2">
                      {ENERGIE_KATEGORIEN.map((kat) => {
                        const selected = form.energie_kategorien.includes(kat.id);
                        return (
                          <motion.button key={kat.id} whileTap={{ scale: 0.96 }}
                            onClick={() => {
                              setForm(prev => ({
                                ...prev,
                                energie_kategorien: selected
                                  ? prev.energie_kategorien.filter(k => k !== kat.id)
                                  : [...prev.energie_kategorien, kat.id]
                              }));
                            }}
                            className="p-2.5 text-left transition-all"
                            style={tagStyle(selected, 'var(--accent-gold)')}
                            data-testid={`feld1-kat-${kat.id}`}
                          >
                            <span className="text-xs font-bold block" style={{ fontFamily: 'var(--font-heading)' }}>{kat.label}</span>
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Seal Button */}
              <motion.button
                whileHover={isValid ? { scale: 1.01, y: -1 } : {}}
                whileTap={isValid ? { scale: 0.98 } : {}}
                onClick={handleSeal}
                disabled={!isValid}
                className="w-full p-4 text-sm font-bold tracking-wide transition-all flex items-center justify-center gap-3"
                style={{
                  background: isValid ? 'var(--accent-cyan)' : 'var(--bg-surface)',
                  color: isValid ? '#fff' : 'var(--text-secondary)',
                  border: 'none',
                  borderRadius: '9999px',
                  cursor: isValid ? 'pointer' : 'not-allowed',
                  boxShadow: isValid ? '0 8px 24px rgba(6,182,212,0.25)' : 'none'
                }}
                data-testid="feld1-seal-btn"
              >
                <ShieldCheck size={18} weight="bold" />
                Identitaet versiegeln
              </motion.button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Feld1Overlay;
